import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/shared/lib/api";
import { toast } from "sonner";
import ScheduleGrid from "@/shared/components/ScheduleGrid";
import RouteEditDialog from "@/shared/components/RouteEditDialog";
import ManageSubscriptionDialog from "@/apps/rider/components/account/ManageSubscriptionDialog";
import { Pause, Play, X, MapPin, ArrowRight, ArrowLeft, Trash2, Pencil, CreditCard, RefreshCw } from "lucide-react";
import Skeleton from "@/shared/motion/Skeleton";
import { formatTime12, formatExplicitDate, formatExplicitDateTime, formatRelativeDate } from "@/shared/lib/format";

export default function Dashboard() {
  const [routes, setRoutes] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [payingRouteId, setPayingRouteId] = useState(null);
  const [sp] = useSearchParams();
  const nav = useNavigate();

  const load = async () => {
    setLoadError(null);
    try {
      const [r, t] = await Promise.all([api.get("/routes/me"), api.get("/trips/me")]);
      setRoutes(r.data);
      setTrips(t.data);
    } catch (e) {
      // Explicit error state instead of an empty-looking dashboard —
      // otherwise a rider whose network dropped sees "No active routes"
      // and thinks their subscription got cancelled. (B2 acceptance:
      // real empty state ≠ real error state.)
      setLoadError(formatApiError(e?.response?.data?.detail) || "We couldn't load your dashboard.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Post-payment confirmation toast — fires once when we arrive with ?welcome=1.
  // Strip the query param so a browser refresh doesn't retrigger it.
  useEffect(() => {
    if (sp.get("welcome") === "1") {
      toast.success("Payment successful — your route is active.", { duration: 5000 });
      nav("/dashboard", { replace: true });
    }
  }, [sp, nav]);

  const togglePause = async (id) => {
    try { const { data } = await api.post(`/routes/${id}/pause`); toast.success(`Route ${data.status}`); load(); }
    catch { toast.error("Action failed"); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel this route and all upcoming trips?")) return;
    try { await api.post(`/routes/${id}/cancel`); toast.success("Route cancelled"); load(); }
    catch { toast.error("Action failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this pending-payment route? This cannot be undone.")) return;
    try { await api.delete(`/routes/${id}`); toast.success("Route deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };
  const manageSubscription = async () => {
    setOpeningPortal(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not open Stripe portal");
    } finally { setOpeningPortal(false); }
  };
  const completePayment = async (id) => {
    setPayingRouteId(id);
    try {
      const { data } = await api.post("/checkout/session", { route_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not open checkout");
      setPayingRouteId(null);
    }
  };

  const visible = routes.filter((r) => r.status !== "cancelled");
  // Billing portal only applies to Stripe subscriptions — not to one-time (PAYG) payments.
  const hasRecurring = visible.some((r) => (r.status === "active" || r.status === "paused") && !r.is_one_time);
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = trips.filter((t) => t.date >= today && t.status === "scheduled").slice(0, 10);

  // Dashboard next-ride card (B1) — surface the single closest scheduled
  // trip at the top of the dashboard so a rider opening the app in a hurry
  // sees "when is my next ride?" without scrolling. Uses the same explicit
  // date formatter as the trip list (Phase A tail #2) so the two surfaces
  // stay linguistically in sync.
  const nextTrip = upcoming[0] || null;

  return (
    <div data-dusk="1" data-testid="user-dashboard-wrapper" style={{ minHeight: "100vh", background: "var(--dusk-bg)" }}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 lg:py-16" data-testid="user-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10 lg:mb-14">
        <div>
          <div className="label-xs text-[#ed6500] mb-3">Dashboard</div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
            Your weeks, handled.
          </h1>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {hasRecurring && (
            <>
              <button onClick={() => setManageOpen(true)} className="btn-outline text-[11px] sm:text-xs tracking-widest uppercase inline-flex items-center gap-2" data-testid="manage-subscription-btn">
                <CreditCard size={14}/> Manage subscription
              </button>
              <button onClick={manageSubscription} disabled={openingPortal} className="text-[10px] sm:text-[11px] tracking-widest uppercase text-[#515E7B] hover:text-[#0A1128] inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="stripe-portal-btn" title="Open Stripe billing portal for invoices/payment methods">
                {openingPortal ? "Opening…" : "Stripe portal"}
              </button>
            </>
          )}
          <a href="/profile" className="btn-outline text-[11px] sm:text-xs tracking-widest uppercase" data-testid="profile-link">Profile</a>
          <a href="/schedule" className="btn-outline text-[11px] sm:text-xs tracking-widest uppercase" data-testid="add-route-btn">+ New route</a>
        </div>
      </div>

      {loading && (
        <div className="space-y-6" data-testid="dashboard-skeleton">
          {/* Next-ride hero placeholder */}
          <Skeleton className="h-24 sm:h-28 w-full rounded-3xl" testid="dashboard-skeleton-next-ride" />
          {/* Two route-block placeholders — matches the shape a returning
              rider expects to see, so the swap into real content feels
              like a fade-in rather than a flash. */}
          <Skeleton className="h-40 w-full rounded-2xl" testid="dashboard-skeleton-route-1" />
          <Skeleton className="h-40 w-full rounded-2xl" testid="dashboard-skeleton-route-2" />
        </div>
      )}

      {!loading && loadError && (
        <div
          role="alert"
          className="border hairline bg-white p-8 sm:p-12 text-center"
          data-testid="dashboard-load-error"
        >
          <div className="font-serif text-2xl sm:text-3xl mb-3 text-[#0A1128]">Couldn&apos;t reach the ride desk</div>
          <p className="text-[#515E7B] mb-6" data-testid="dashboard-load-error-detail">{loadError}</p>
          <button
            type="button"
            onClick={() => { setLoading(true); load(); }}
            className="btn-primary text-xs tracking-widest uppercase inline-flex items-center gap-2"
            data-testid="dashboard-load-retry"
          >
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      )}

      {!loading && !loadError && visible.length === 0 && (
        <div className="border hairline bg-white p-10 sm:p-16 text-center" data-testid="no-routes">
          <div className="font-serif text-3xl mb-4">No active routes yet.</div>
          <p className="text-[#515E7B] mb-8">Design your first weekly plan and we&apos;ll handle the rest.</p>
          <a href="/schedule" className="btn-primary text-xs tracking-widest uppercase" data-testid="dashboard-empty-cta">Design your route</a>
        </div>
      )}

      {/* Next-ride hero — only when we actually have a scheduled trip. Kept
          tight (single row on desktop, wraps on mobile) so it never crowds
          the route blocks below. Deep-links to /trips for the full stepper. */}
      {!loading && !loadError && nextTrip && (
        <a
          href="/trips"
          className="group relative flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 rounded-3xl bg-[#0A1128] text-[#F9F7F4] p-5 sm:p-6 mb-10 lg:mb-14 overflow-hidden transition-transform hover:scale-[1.002]"
          data-testid="dashboard-next-ride"
        >
          <div className="absolute -right-12 -top-12 w-56 h-56 bg-[#ed6500]/12 rounded-full blur-3xl" aria-hidden="true" />
          <div className="relative min-w-0 flex-1">
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500]">Next ride</div>
            <div className="font-serif text-2xl sm:text-3xl leading-tight mt-1 tabular-nums" data-testid="dashboard-next-ride-when">
              {formatRelativeDate(nextTrip.date)} · {formatTime12(nextTrip.pickup_time)}
            </div>
            <div className="flex items-center gap-2 text-sm mt-2 min-w-0 text-[#F9F7F4]/85">
              <span className="w-1.5 h-1.5 rounded-full bg-[#5c97f7] shrink-0" />
              <span className="truncate max-w-[220px]">{(nextTrip.pickup_address || "").split(",")[0]}</span>
              <ArrowRight size={12} className="text-[#ed6500] shrink-0" />
              <span className="w-1.5 h-1.5 rounded-full bg-[#ed6500] shrink-0" />
              <span className="truncate max-w-[220px]">{(nextTrip.dropoff_address || "").split(",")[0]}</span>
            </div>
          </div>
          <div className="relative text-[11px] tracking-[0.2em] uppercase font-semibold text-[#F9F7F4]/80 inline-flex items-center gap-1 group-hover:text-white transition-colors">
            View ride <ArrowRight size={12} />
          </div>
        </a>
      )}

      {!loading && !loadError && (
      <div className="space-y-12 lg:space-y-16">
        {visible.map((r) => {
          const isPending = r.status === "pending_payment";
          const isExpired = r.status === "expired";
          const statusLabel = r.status === "active" ? "Active"
            : r.status === "paused" ? "Paused"
            : r.status === "expired" ? "Expired"
            : r.status === "pending_payment" ? "Pending payment"
            : r.status.replace("_", " ");
          const statusColor = r.status === "active" ? "text-[#5c97f7]"
            : r.status === "paused" ? "text-[#ed6500]"
            : r.status === "expired" ? "text-[#ed6500]"
            : "text-[#ed6500]";
          return (
            <div key={r.id} className="space-y-5" data-testid={`route-block-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4 border-b hairline pb-5">
                <div className="space-y-2 min-w-0">
                  <div className={`label-xs ${statusColor}`}>
                    {statusLabel}
                    {r.is_one_time && <span className="ml-2 text-[9px] font-semibold tracking-widest text-[#ed6500]">ONE-TIME</span>}
                  </div>
                  <div className="font-serif text-2xl sm:text-3xl flex items-start gap-3">
                    <MapPin size={20} className="mt-2 text-[#515E7B] shrink-0" />
                    <span className="min-w-0 break-words">{r.pickup_address} <span className="text-[#515E7B]">→</span> {r.destination_address}</span>
                  </div>
                  <div className="text-sm text-[#515E7B] flex gap-4 flex-wrap">
                    <span>Pickup {formatTime12(r.pickup_time)}</span>
                    <span>Leave work {formatTime12(r.return_time)}</span>
                    <span className="uppercase">{r.tier_id} · ${r.daily_rate?.toFixed(0)}/day <span className="text-[10px] text-[#515E7B]">({r.pricing_type || "daily"})</span></span>
                    <span className="capitalize">{r.billing_cycle} · ${r.cycle_amount?.toFixed(2)}{r.is_one_time ? " one-time" : "/cycle"}</span>
                    {r.is_one_time && r.paid_through_date && r.status === "active" && (
                      <span className="text-[#5c97f7]">Paid through {formatExplicitDate(r.paid_through_date, { withYear: true })}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {isPending ? (
                    <>
                      <button
                        onClick={() => completePayment(r.id)}
                        disabled={payingRouteId === r.id}
                        className="btn-primary text-[11px] sm:text-xs tracking-widest uppercase inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid={`complete-payment-${r.id}`}
                      >
                        <CreditCard size={14}/> {payingRouteId === r.id ? "Opening…" : "Complete payment"}
                      </button>
                      <button onClick={() => remove(r.id)} className="btn-outline text-[11px] sm:text-xs tracking-widest uppercase inline-flex items-center gap-2 border-[#ed6500] text-[#ed6500] hover:bg-[#ed6500] hover:text-white" data-testid={`delete-${r.id}`}>
                        <Trash2 size={14}/> Delete
                      </button>
                    </>
                  ) : isExpired ? (
                    <>
                      <button
                        onClick={() => completePayment(r.id)}
                        disabled={payingRouteId === r.id}
                        className="btn-primary text-[11px] sm:text-xs tracking-widest uppercase inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid={`renew-${r.id}`}
                      >
                        <RefreshCw size={14}/> {payingRouteId === r.id ? "Opening…" : "Renew"}
                      </button>
                      <button onClick={() => cancel(r.id)} className="btn-outline text-[11px] sm:text-xs tracking-widest uppercase inline-flex items-center gap-2 border-[#ed6500] text-[#ed6500] hover:bg-[#ed6500] hover:text-white" data-testid={`cancel-${r.id}`}>
                        <X size={14}/> Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditing(r)} className="btn-outline text-[11px] sm:text-xs tracking-widest uppercase inline-flex items-center gap-2" data-testid={`edit-${r.id}`}>
                        <Pencil size={14}/> Edit
                      </button>
                      <button onClick={() => togglePause(r.id)} className="btn-outline text-[11px] sm:text-xs tracking-widest uppercase inline-flex items-center gap-2" data-testid={`pause-${r.id}`}>
                        {r.status === "active" ? <><Pause size={14}/> Pause</> : <><Play size={14}/> Resume</>}
                      </button>
                      <button onClick={() => cancel(r.id)} className="btn-outline text-[11px] sm:text-xs tracking-widest uppercase inline-flex items-center gap-2 border-[#ed6500] text-[#ed6500] hover:bg-[#ed6500] hover:text-white" data-testid={`cancel-${r.id}`}>
                        <X size={14}/> Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>

              <ScheduleGrid
                days={r.days}
                pickup_time={r.pickup_time}
                return_time={r.return_time}
                pickup_address={r.pickup_address}
                destination_address={r.destination_address}
                dayOverrides={r.day_overrides || {}}
                buffer={r.buffer_minutes || 30}
              />
            </div>
          );
        })}
      </div>
      )}

      {!loading && !loadError && upcoming.length > 0 && (
        <div className="mt-16 lg:mt-20">
          <div className="label-xs text-[#ed6500] mb-4">Next 10 trips</div>
          <div className="border hairline bg-white divide-y divide-[#E5E1D8]" data-testid="upcoming-trips">
            {upcoming.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-4 sm:p-5 hover:bg-[#F9F7F4] transition-colors" data-testid={`upcoming-trip-${t.id}`}>
                <div className="flex items-center gap-4 sm:gap-6 min-w-0">
                  <div className="font-serif text-xl sm:text-2xl w-20 sm:w-28">{formatTime12(t.pickup_time)}</div>
                  {/* Always show the explicit calendar date next to the time so a rider
                      never has to guess "which Monday?". Relative labels ("Today",
                      "Tomorrow") ride alongside the explicit date on the first 48h. */}
                  <div className="label-xs text-[#515E7B] w-32 sm:w-40 hidden sm:block" data-testid={`upcoming-trip-date-${t.id}`}>
                    {formatRelativeDate(t.date)}
                  </div>
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    {t.direction === "outbound"
                      ? <ArrowRight size={14} className="text-[#ed6500] shrink-0" />
                      : <ArrowLeft size={14} className="text-[#5c97f7] shrink-0" />}
                    <span className="truncate max-w-[180px] sm:max-w-xs">{t.pickup_address}</span>
                    <span className="text-[#515E7B]">→</span>
                    <span className="truncate max-w-[180px] sm:max-w-xs">{t.dropoff_address}</span>
                  </div>
                </div>
                <div className="label-xs text-[#515E7B] hidden sm:block">{t.direction}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <RouteEditDialog key={editing?.id || "none"} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} route={editing} onSaved={load} />
      <ManageSubscriptionDialog open={manageOpen} onClose={() => setManageOpen(false)} onChanged={load} />
    </div>
    </div>
  );
}
